import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "./useAuth";

export interface UserPermissions {
  // Boolean permissions
  comprar_relatorios: boolean;
  criar_negocios: boolean;
  excluir_negocios: boolean;
  exportar_dados: boolean;
  gerenciar_usuarios: boolean;
  visualizar_dashboard: boolean;
  visualizar_faturamento: boolean;
  visualizar_cobrancas: boolean;
  // Enum permissions
  visualizar_empresas: "nenhuma" | "apenas_proprias" | "todas";
  visualizar_negocios: "nenhum" | "apenas_proprios" | "todos";
  editar_negocios: "nenhum" | "apenas_proprios" | "todos";
}

const DEFAULT_PERMISSIONS: UserPermissions = {
  comprar_relatorios: false,
  criar_negocios: false,
  excluir_negocios: false,
  exportar_dados: false,
  gerenciar_usuarios: false,
  visualizar_dashboard: false,
  visualizar_faturamento: false,
  visualizar_cobrancas: false,
  visualizar_empresas: "nenhuma",
  visualizar_negocios: "nenhum",
  editar_negocios: "nenhum",
};

// Full permissions for admins
const ADMIN_PERMISSIONS: UserPermissions = {
  comprar_relatorios: true,
  criar_negocios: true,
  excluir_negocios: true,
  exportar_dados: true,
  gerenciar_usuarios: true,
  visualizar_dashboard: true,
  visualizar_faturamento: true,
  visualizar_cobrancas: true,
  visualizar_empresas: "todas",
  visualizar_negocios: "todos",
  editar_negocios: "todos",
};

export function usePermissions() {
  const { user, tenantRole, isLoading: authLoading, isAdmin, isSuperAdmin } = useAuth();

  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ["user-permissions", user?.id],
    queryFn: async (): Promise<UserPermissions> => {
      if (!user?.id) return DEFAULT_PERMISSIONS;

      // First get the user's permissoes_id from the users table
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("permissoes_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (userError || !userData?.permissoes_id) {
        logger.error("Error fetching user permissoes_id", userError);
        return DEFAULT_PERMISSIONS;
      }

      // Then get the permissions from controle_permissoes
      const { data: permData, error: permError } = await supabase
        .from("controle_permissoes")
        .select("*")
        .eq("id", userData.permissoes_id)
        .maybeSingle();

      if (permError || !permData) {
        logger.error("Error fetching permissions", permError);
        return DEFAULT_PERMISSIONS;
      }

      return {
        comprar_relatorios: permData.comprar_relatorios ?? false,
        criar_negocios: permData.criar_negocios ?? false,
        excluir_negocios: permData.excluir_negocios ?? false,
        exportar_dados: permData.exportar_dados ?? false,
        gerenciar_usuarios: permData.gerenciar_usuarios ?? false,
        visualizar_dashboard: permData.visualizar_dashboard ?? false,
        visualizar_faturamento: permData.visualizar_faturamento ?? false,
        visualizar_cobrancas: permData.visualizar_cobrancas ?? false,
        visualizar_empresas: (permData.visualizar_empresas as UserPermissions["visualizar_empresas"]) ?? "nenhuma",
        visualizar_negocios: (permData.visualizar_negocios as UserPermissions["visualizar_negocios"]) ?? "nenhum",
        editar_negocios: (permData.editar_negocios as UserPermissions["editar_negocios"]) ?? "nenhum",
      };
    },
    enabled: !!user?.id && !isAdmin && !isSuperAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Admins and SuperAdmins have full permissions
  const effectivePermissions = (isAdmin || isSuperAdmin) ? ADMIN_PERMISSIONS : (permissions ?? DEFAULT_PERMISSIONS);

  // Computed helpers for common permission checks
  const hasAdminRole = tenantRole === "Owner" || tenantRole === "Admin" || isAdmin || isSuperAdmin;
  const canViewCompanies = hasAdminRole || effectivePermissions.visualizar_empresas !== "nenhuma";
  const canViewReports = hasAdminRole || effectivePermissions.visualizar_negocios !== "nenhum";
  const canViewBilling = hasAdminRole || effectivePermissions.visualizar_faturamento;
  const canViewDashboard = hasAdminRole || effectivePermissions.visualizar_dashboard;
  const canExportData = hasAdminRole || effectivePermissions.exportar_dados;
  const canPurchaseReports = hasAdminRole || effectivePermissions.comprar_relatorios;
  const canManageUsers = hasAdminRole || effectivePermissions.gerenciar_usuarios;
  const canViewSettings = hasAdminRole || effectivePermissions.gerenciar_usuarios;
  
  // Collections permissions
  const canViewCollections = hasAdminRole || effectivePermissions.visualizar_cobrancas;
  const canCreateCollections = true; // All authenticated tenant members can create
  const canEditCollections = tenantRole === "Owner" || isAdmin || isSuperAdmin;

  return {
    permissions: effectivePermissions,
    isLoading: authLoading || permissionsLoading,
    // Computed permission helpers
    canViewCompanies,
    canViewReports,
    canViewBilling,
    canViewDashboard,
    canExportData,
    canPurchaseReports,
    canManageUsers,
    canViewSettings,
    hasAdminRole,
    // Collections
    canViewCollections,
    canCreateCollections,
    canEditCollections,
  };
}
